import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from '../BasicElement';
import Image, { IImage } from '../Image';
import Modal from '../Modal';

export interface IVideo {
	src: string;
}

export interface IVideoProps extends IBaseElementProps {
	url?: IVideo;
	title?: string;
	width?: number;
	height?: number;
	/**
	 * You can paste embed code from video streaming sites like YouTube or Vimeo here.
	 * @control textarea
	 */
	oembed?: string;
	poster?: IImage;
	playLabel?: string;
	viewLabel?: string;
	autoPlay?: boolean;
	style: 'modal' | 'inline';
}

export const getYoutubeIDFromURL = (url: string): string | boolean => {
	var regExp =
		/^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
	var match = url.match(regExp);
	return match && match[7].length == 11 ? match[7] : false;
};

const Video: React.FC<IVideoProps> = ({
	className,
	classNames = [],
	url,
	title,
	width,
	height,
	oembed,
	poster,
	playLabel = 'Play video',
	viewLabel = 'View image',
	autoPlay = false,
	style = 'modal',
}) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const ref = useRef(null);
	let embedURL = '';
	let thumbURL = poster ? poster.src : '';

	useEffect(() => {
		if (!ref || !ref.current) {
			return;
		}

		if (url && !oembed) {
			const video = ref.current! as HTMLVideoElement;
			video.addEventListener('play', () => {
				setIsPlaying(true);
			});

			video.addEventListener('pause', () => {
				setIsPlaying(false);
			});
		}
	}, [ref, url, oembed]);

	if (oembed) {
		const urlMatches = oembed!.match(/\bhttps?:\/\/\S+/gi);
		if (urlMatches && urlMatches.length > 0) {
			embedURL = urlMatches[0];

			if (
				embedURL.indexOf('youtube.com/') > -1 ||
				embedURL.indexOf('youtu.be/') > -1
			) {
				if (!thumbURL) {
					thumbURL = `https://i1.ytimg.com/vi/${getYoutubeIDFromURL(
						embedURL,
					)}/hqdefault.jpg`;
				}
			}
		}
	}

	return (
		<div
			className={
				className ||
				cn(
					'video',
					isPlaying && 'video--playing',
					thumbURL && 'video--poster',
					...classNames,
				)
			}
			style={
				(thumbURL && !isPlaying)
					? { backgroundImage: `url("${thumbURL}")` }
					: {}
			}
		>
			{style && style === 'modal' && url && url.src && !oembed && showModal && (
				<Modal
					className="vhs-video-modal  modal--video"
					onCancel={() => {
						setShowModal(false);
					}}
					headerClassNames={[]}
					bodyClassNames={[]}
					showCancel={false}
					showSubmit={false}
				>
					<video
						ref={ref}
						className="video__player"
						controls
						autoPlay={autoPlay}
						poster={thumbURL ? thumbURL : undefined}
					>
						<source src={url.src} type="video/mp4" />
						Your browser does not support the video tag.
					</video>
				</Modal>
			)}

			{style && style === 'inline' && url && url.src && !oembed && showModal && (
				<div className="vhs-video-inline">
					<div className="video__embed">
						<iframe src={url.src + '&autoplay=' + autoPlay}  width={width} height={height} allow="autoplay; fullscreen; picture-in-picture" title={title}></iframe>
					</div>
				</div>
			)}

			{style && style === 'modal' && oembed && showModal && (
				<Modal
					className="vhs-video-modal  vhs-video-modal--video modal--video"
					onCancel={() => setShowModal(false)}
					headerClassNames={[]}
					bodyClassNames={[]}
					showCancel={false}
					showSubmit={false}
				>
					<div
						className="video__embed"
						dangerouslySetInnerHTML={{
							__html: `${oembed}`,
						}}
					/>
				</Modal>
			)}

			{style && style === 'inline' && oembed && showModal && (
				<div className="vhs-video-inline">
					<div
						className="video__embed"
						dangerouslySetInnerHTML={{
							__html: `${oembed}`,
						}}
					/>
				</div>
			)}

			{style && style === 'modal' && !url && !oembed && showModal && (
				<Modal
					className="vhs-video-modal vhs-video-modal--image  modal--image"
					onCancel={() => setShowModal(false)}
					headerClassNames={[]}
					bodyClassNames={[]}
					showCancel={false}
					showSubmit={false}
				>
					<Image {...poster} className="image" />
				</Modal>
			)}

			{((url && url.src) || oembed) && !isPlaying && (
				<button
					className="btn-play"
					onClick={() => {
						setShowModal(true);
						if (!oembed) {
							ref.current &&
								(ref.current! as HTMLVideoElement).play();
						}
						if (style && style === 'inline') {
							setIsPlaying(true)
						}
					}}
				>
					{playLabel}
				</button>
			)}

			{style && style === 'modal' && !url && !url?.src && !oembed && (
				<button
					className="btn-expand"
					onClick={() => {
						setShowModal(true);
					}}
				>
					{viewLabel}
				</button>
			)}
		</div>
	);
};

export default Video;
